// ============================================================================
// P4-CC26c-0b-2 — the chart of accounts is created at FIRST AUTHENTICATED
// CONTACT, because it cannot be created any earlier.
//
// ── WHY NOT AT SIGNUP, WHERE IT OBVIOUSLY BELONGS ───────────────────────────
// MEASURED, not assumed (P4-CC26c-0b-probe, 17 Aug 2026): inside
// handle_new_user() — the one and only path that creates a company —
// auth.uid() is NULL. session_user is supabase_auth_admin, the membership row
// already exists (so ordering is not the problem), and there is simply no
// session. current_company_id() reads auth.uid() and nothing else, so it can
// never resolve there, and set_company_from_auth() raises 42501 rather than
// guessing at a company.
//
// Seeding at company creation therefore requires weakening the tenancy guard.
// We will not weaken it. This is the substitute, and the trade is explicit: a
// company has no chart for the few seconds between signup and its first
// authenticated request, and then it has one, permanently.
//
// The 11 companies that existed before this shipped were fixed by the one-off
// backfill in 20260817120000_cc26c0b1_backfill_charts.sql. This file is only
// about companies created from now on.
//
// ── WHY THE LOGIC IS HERE AND NOT IN AuthProvider ──────────────────────────
// A call made only inside a React component is invisible to the verification
// estate and cannot be exercised by it. Every rule that matters — the
// idempotency, the permission tolerance, the company check — lives in this
// module so supabase/verify/gl_chart_bootstrap.verify.mjs can drive it directly
// against the live database. AuthProvider does nothing but call it.
// ============================================================================

import { supabase } from "./supabaseClient";
import { getMyMembership } from "./auth";

// ── The per-session bound ───────────────────────────────────────────────────
// AuthProvider.loadMembership() is NOT called once per session. It runs on
// mount, on EVERY supabase auth-state event (including the roughly hourly
// TOKEN_REFRESHED), and again whenever refreshMembership() is invoked — Business
// Settings calls it after every save. Left unbounded this would fire an RPC on
// each of those.
//
// A Set of company ids, not a boolean: a user can sign out and sign in as
// somebody else in a DIFFERENT company without reloading the page — which is
// exactly why clearCompanyCache() exists in db.js — and a boolean would skip
// the second company's chart entirely.
//
// Deliberately a plain module-level Set rather than anything cleverer. It lives
// as long as the page does, which is the same lifetime as the problem it solves.
const ensuredCompanies = new Set();

/** Test-only. Nothing in the application should call this. */
export function _resetEnsuredCache() {
  ensuredCompanies.clear();
}

/**
 * Make sure the caller's company has a chart of accounts.
 *
 * Safe to call on every auth event: the seeding RPC is idempotent server-side
 * (it returns 0 when the chart already exists) and this module additionally
 * remembers which companies it has already confirmed.
 *
 * @param {string} [companyId] the caller's company, if it is already known.
 *   AuthProvider has just resolved it, so passing it avoids a second round trip.
 *   Omitted, it is resolved with getMyMembership() — the same shape as
 *   getMyMembership(userId) itself.
 * @returns {Promise<{created:number}|{skipped:true, reason:string}>}
 */
export async function ensureChartOfAccounts(companyId) {
  if (!supabase) return { skipped: true, reason: "not-configured" };

  const cid = companyId || (await getMyMembership())?.company_id || null;

  // No company resolved means no session, or a user with no membership. Do
  // nothing at all — there is nothing to seed and nothing has gone wrong.
  if (!cid) return { skipped: true, reason: "no-company" };

  if (ensuredCompanies.has(cid)) return { skipped: true, reason: "already-ensured" };

  const { data, error } = await supabase.rpc("seed_chart_of_accounts");

  if (error) {
    // ⚠️ EXACTLY ONE CODE IS TOLERATED, AND ONLY THIS ONE.
    // seed_chart_of_accounts() requires can_write('accounts') — owner, admin and
    // accountant. A company's first login is almost always its owner, so this
    // should never bite. But if a SALES user is ever first through the door they
    // have done nothing wrong and must not be shown an error for it.
    //
    // NOT added to ensuredCompanies: this company still has no chart, and the
    // next better-privileged session in the same page load must be allowed to
    // create one. Marking it done here would be a silent, permanent miss.
    if (error.code === "42501") return { skipped: true, reason: "no-permission" };

    // ⚠️ EVERYTHING ELSE PROPAGATES, DELIBERATELY.
    // A catch-all here would turn a real failure — a dropped function, a broken
    // migration, a network fault — into a silent no-op, and nobody would find
    // out until an invoice refused to post months later with no account to post
    // it to. The caller decides what to show; this layer does not decide to hide.
    throw error;
  }

  ensuredCompanies.add(cid);
  return { created: data ?? 0 };
}

/**
 * The company's chart of accounts, ordered by code (P4-CC26d-0).
 *
 * ⚠️ THREE OUTCOMES, NOT TWO — AND THE MIDDLE ONE IS WHY THIS FUNCTION EXISTS.
 *   { accounts: [...] }                        rows, possibly zero
 *   { skipped: true, reason: "no-permission" } the caller may not read accounts
 *   { skipped: true, reason: "no-company" }    no session, or no membership
 *
 * sales, driver and employee have NO read on accounts, and a denied read under
 * RLS returns ZERO ROWS WITH NO ERROR — the policy's USING clause simply hides
 * every row. A naive implementation reports that as "this company has no
 * accounts", which is a lie: it tells a user their books are empty when the
 * truth is that they are not allowed to look.
 *
 * ── HOW "DENIED" IS TOLD FROM "EMPTY", AND WHY IT IS RELIABLE ──────────────
 * By asking can_read('accounts') — THE SAME FUNCTION THE POLICY ITSELF CALLS.
 * The RBAC read policy is built as
 *     using (company_id in (select public.user_company_ids())
 *            and public.can_read('accounts'))
 * (20260730130000_role_permissions.sql:385), and can_read(text) is granted to
 * authenticated (:447). So this is not an inference about permissions and not a
 * second copy of the 141-row matrix — it is the same oracle, consulted directly.
 * If the matrix ever changes, the policy and this check change together because
 * they are the same code. That is what makes it reliable; a role-name lookup in
 * JavaScript would be a copy free to drift.
 *
 * It is only consulted when the select comes back EMPTY, so the ordinary case
 * costs one round trip and only the genuinely ambiguous case costs two.
 *
 * ⛔ READ ONLY. No create, update or delete here — writing accounts needs
 * RBAC-aware plumbing and its own harness (CC26d-1).
 */
export async function listAccounts(companyId) {
  if (!supabase) return { skipped: true, reason: "not-configured" };

  const cid = companyId || (await getMyMembership())?.company_id || null;
  if (!cid) return { skipped: true, reason: "no-company" };

  // Everything the screen needs to render, and nothing it does not.
  const { data, error } = await supabase
    .from("accounts")
    .select("id, code, name, type, subtype, is_system, is_active, parent_id")
    .eq("company_id", cid)
    .order("code", { ascending: true });

  // A real failure — a dropped table, a network fault — must reach the caller.
  // Only the empty case is ambiguous, and it is resolved below rather than
  // guessed at.
  if (error) throw error;

  const rows = data || [];
  if (rows.length > 0) return { accounts: rows };

  const { data: allowed, error: permErr } = await supabase.rpc("can_read", { p_resource: "accounts" });
  if (permErr) throw permErr;
  if (allowed === false) return { skipped: true, reason: "no-permission" };

  // Permitted and genuinely empty: a company whose chart has never been seeded.
  return { accounts: [] };
}
